import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Info, Plus, Pencil, Trash2, Sparkles, Check, X, Mic, Square, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TOOLTIP_TEXT = "Espacio para registrar contexto relevante de la empresa que facilite continuidad comercial y comprensión estratégica. No utilizar para tareas ni seguimiento operativo.";

const AccountHistorySection = ({ companyId, typsaOrgUnitId }) => {
  const { isEditor, isAdmin, currentUser } = useUser();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEntryText, setNewEntryText] = useState('');
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [saving, setSaving] = useState(false);
  const [showNewEntryForm, setShowNewEntryForm] = useState(false);
  
  // AI improvement state
  const [improvingText, setImprovingText] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [improvedText, setImprovedText] = useState('');
  const [improvementTarget, setImprovementTarget] = useState('new');
  
  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Determine the entity type and ID
  const entityType = typsaOrgUnitId ? 'typsa-org-units' : 'companies';
  const entityId = typsaOrgUnitId || companyId;

  useEffect(() => {
    fetchEntries();
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, [entityId]);

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/${entityType}/${entityId}/account-history`, {
        headers: { 'x-user-email': currentUser?.email }
      });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching account history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Audio Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const transcribeAudio = async (blob) => {
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', blob, 'recording.webm');
      
      const response = await axios.post(`${API}/utils/transcribe-audio`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-user-email': currentUser?.email
        }
      });
      
      const transcript = response.data.transcript;
      if (transcript) {
        setNewEntryText(prev => prev ? `${prev}\n\n${transcript}` : transcript);
        toast.success('Audio transcrito correctamente');
        setAudioBlob(null);
      }
    } catch (error) {
      console.error('Error transcribing audio:', error);
      toast.error('Error al transcribir el audio');
    } finally {
      setTranscribing(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setTranscribing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/utils/transcribe-audio`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-user-email': currentUser?.email
        }
      });
      
      const transcript = response.data.transcript;
      if (transcript) {
        setNewEntryText(prev => prev ? `${prev}\n\n${transcript}` : transcript);
        toast.success('Audio transcrito correctamente');
      }
    } catch (error) {
      console.error('Error transcribing file:', error);
      toast.error('Error al transcribir el archivo');
    } finally {
      setTranscribing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleCreateEntry = async () => {
    if (!newEntryText.trim()) return;
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/${entityType}/${entityId}/account-history`,
        { content: newEntryText },
        { headers: { 'x-user-email': currentUser?.email } }
      );
      setNewEntryText('');
      setShowNewEntryForm(false);
      setAudioBlob(null);
      fetchEntries();
      toast.success('Entrada creada correctamente');
    } catch (error) {
      console.error('Error creating entry:', error);
      toast.error('Error al crear la entrada');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEntry = async (entryId) => {
    if (!editingText.trim()) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API}/${entityType}/${entityId}/account-history/${entryId}`,
        { content: editingText },
        { headers: { 'x-user-email': currentUser?.email } }
      );
      setEditingEntryId(null);
      setEditingText('');
      fetchEntries();
      toast.success('Entrada actualizada correctamente');
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Error al actualizar la entrada');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async () => {
    if (!entryToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/${entityType}/${entityId}/account-history/${entryToDelete.id}`,
        { headers: { 'x-user-email': currentUser?.email } }
      );
      fetchEntries();
      toast.success('Entrada eliminada correctamente');
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Error al eliminar la entrada');
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setEntryToDelete(null);
    }
  };

  const handleImproveText = async (text, target) => {
    if (!text.trim()) {
      toast.error('No hay texto para mejorar');
      return;
    }
    
    setImprovingText(true);
    setImprovementTarget(target);
    
    try {
      const response = await axios.post(
        `${API}/account-history/improve-text`,
        { text, context: 'account_history' },
        { headers: { 'x-user-email': currentUser?.email } }
      );
      setImprovedText(response.data.improved);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error('Error improving text:', error);
      toast.error('Error al mejorar el texto');
    } finally {
      setImprovingText(false);
    }
  };

  const applyImprovedText = () => {
    if (improvementTarget === 'new') {
      setNewEntryText(improvedText);
    } else {
      setEditingText(improvedText);
    }
    setShowPreviewDialog(false);
    setImprovedText('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Card className="mt-6">
        <CardContent className="py-8 text-center text-muted-foreground">
          Cargando historial de cuenta...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6" data-testid="account-history-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle className="text-lg font-medium">Historial de Cuenta</CardTitle>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">{TOOLTIP_TEXT}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          {isEditor && !showNewEntryForm && (
            <Button
              size="sm"
              onClick={() => setShowNewEntryForm(true)}
              data-testid="new-account-history-entry-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Nueva entrada
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* New Entry Form */}
        {showNewEntryForm && (
          <div className="mb-6 p-4 border rounded-lg bg-muted/30">
            <Textarea
              placeholder="Describe información relevante sobre esta cuenta (estilo de trabajo, decisores clave, histórico, etc.)..."
              value={newEntryText}
              onChange={(e) => setNewEntryText(e.target.value)}
              className="min-h-[120px] mb-3"
              data-testid="new-account-history-textarea"
            />
            
            {/* Audio Controls */}
            <div className="flex flex-wrap items-center gap-2 mb-3 p-3 bg-background rounded-md border">
              <span className="text-xs text-muted-foreground mr-2">Audio:</span>
              
              {!isRecording && !audioBlob && !transcribing && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={startRecording}
                    className="gap-1"
                    data-testid="start-recording-btn"
                  >
                    <Mic className="w-4 h-4 text-red-500" />
                    <span className="hidden sm:inline">Grabar</span>
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="gap-1"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="hidden sm:inline">Subir</span>
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                </>
              )}
              
              {isRecording && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                    <span className="text-sm font-medium">{formatTime(recordingTime)}</span>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    onClick={stopRecording}
                    className="gap-1"
                    data-testid="stop-recording-btn"
                  >
                    <Square className="w-4 h-4" />
                    Detener
                  </Button>
                </div>
              )}
              
              {audioBlob && !transcribing && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">Audio grabado</Badge>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => transcribeAudio(audioBlob)}
                    className="gap-1"
                    data-testid="transcribe-btn"
                  >
                    <Sparkles className="w-4 h-4" />
                    Transcribir
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => setAudioBlob(null)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              {transcribing && (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Transcribiendo...</span>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={handleCreateEntry}
                disabled={!newEntryText.trim() || saving}
                data-testid="save-account-history-entry-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Guardar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleImproveText(newEntryText, 'new')}
                disabled={!newEntryText.trim() || improvingText}
              >
                {improvingText && improvementTarget === 'new' 
                  ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> 
                  : <Sparkles className="w-4 h-4 mr-1" />
                }
                Mejorar redacción
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setShowNewEntryForm(false);
                  setNewEntryText('');
                  setAudioBlob(null);
                }}
              >
                <X className="w-4 h-4 mr-1" />
                Cancelar
              </Button>
            </div>
          </div>
        )}

        {/* Entries Timeline */}
        {entries.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No hay entradas en el historial de cuenta.</p>
            {isEditor && !showNewEntryForm && (
              <Button
                variant="link"
                onClick={() => setShowNewEntryForm(true)}
                className="mt-2"
              >
                Añadir primera entrada
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {entries.map((entry) => (
              <div
                key={entry.id}
                className="relative pl-6 pb-4 border-l-2 border-muted last:pb-0"
                data-testid={`account-history-entry-${entry.id}`}
              >
                {/* Timeline dot */}
                <div className="absolute left-[-5px] top-0 w-2 h-2 rounded-full bg-primary" />
                
                {editingEntryId === entry.id ? (
                  // Edit mode
                  <div className="space-y-3">
                    <Textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="min-h-[100px]"
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleUpdateEntry(entry.id)}
                        disabled={saving}
                      >
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                        Guardar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleImproveText(editingText, entry.id)}
                        disabled={!editingText.trim() || improvingText}
                      >
                        {improvingText && improvementTarget === entry.id 
                          ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> 
                          : <Sparkles className="w-4 h-4 mr-1" />
                        }
                        Mejorar
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingEntryId(null);
                          setEditingText('');
                        }}
                      >
                        <X className="w-4 h-4 mr-1" />
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <div>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 text-xs text-muted-foreground">
                        <span className="font-medium">{entry.created_by_name}</span>
                        <span className="hidden sm:inline">•</span>
                        <span>{formatDate(entry.created_at)}</span>
                        {entry.has_audio && (
                          <Badge variant="secondary" className="text-xs">
                            <Mic className="w-3 h-3 mr-1" />
                            Audio
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {isEditor && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            onClick={() => {
                              setEditingEntryId(entry.id);
                              setEditingText(entry.content);
                            }}
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        )}
                        {isAdmin && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            onClick={() => {
                              setEntryToDelete(entry);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-account-history-entry-${entry.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{entry.content}</p>
                    {entry.updated_at && (
                      <p className="text-xs text-muted-foreground mt-2 italic">
                        Editado por {entry.updated_by_name} el {formatDate(entry.updated_at)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar entrada?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer. La entrada será eliminada permanentemente.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteEntry}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* AI Preview Dialog */}
        <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Vista previa del texto mejorado</DialogTitle>
              <DialogDescription>
                Revisa el texto mejorado por IA antes de aplicarlo. No se ha inventado información.
              </DialogDescription>
            </DialogHeader>
            <div className="p-4 bg-muted rounded-md">
              <p className="whitespace-pre-wrap text-sm">{improvedText}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
                Cancelar
              </Button>
              <Button onClick={applyImprovedText}>
                <Check className="w-4 h-4 mr-1" />
                Aplicar texto
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default AccountHistorySection;
